
import React, { useState } from 'react';
import { ReferralURLConfig } from '@/utils/config/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link, PlusCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { deleteReferralURL, saveReferralURL } from '@/utils/config';
import ReferralURLCard from './referral/url/ReferralURLCard';
import ReferralURLAddEditForm from './referral/url/ReferralURLAddEditForm';

interface ReferralURLFormProps {
  urls: ReferralURLConfig[];
  onSaved: () => void;
}

const ReferralURLForm: React.FC<ReferralURLFormProps> = ({
  urls,
  onSaved
}) => {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const handleEdit = (url: ReferralURLConfig) => {
    setEditingId(url.id);
    setShowAddForm(true);
  };
  
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this URL?')) {
      try {
        await deleteReferralURL(id);
        toast({
          title: 'URL deleted',
          description: 'The referral URL has been deleted successfully',
        });
        onSaved();
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to delete URL',
          variant: 'destructive',
        });
      }
    }
  };
  
  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    
    try {
      if (!editingId && urls.length >= 3) {
        toast({
          title: 'Error',
          description: 'Maximum of 3 referral URLs allowed',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }
      
      const configToSave: ReferralURLConfig = {
        id: editingId || String(Date.now()),
        name: data.name,
        baseUrl: data.url,
        isActive: true,
        isDefault: data.isDefault,
        banners: editingId 
          ? (urls.find(u => u.id === editingId)?.banners || [])
          : [],
      };
      
      await saveReferralURL(configToSave);
      
      toast({
        title: 'Success',
        description: `Referral URL ${editingId ? 'updated' : 'added'} successfully`,
      });
      
      handleCancel();
      onSaved();
      
    } catch (error) {
      console.error('Error saving URL:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save URL',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleBannerUpdate = async (urlId: string, newBanners: any[]) => {
    try {
      const updatedUrls = urls.map(url => 
        url.id === urlId 
          ? { ...url, banners: newBanners }
          : url
      );
      
      const urlToUpdate = updatedUrls.find(u => u.id === urlId);
      if (urlToUpdate) {
        await saveReferralURL(urlToUpdate);
        onSaved();
      }
    } catch (error) {
      console.error('Error updating banners:', error);
      toast({
        title: "Error",
        description: "Failed to update referral banners",
        variant: "destructive",
      });
    }
  };
  
  const handleAddNew = () => {
    setEditingId(null);
    setShowAddForm(true);
  };
  
  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Referral URLs</h3>
        {!showAddForm && urls.length < 3 && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleAddNew}
            className="flex items-center gap-1"
          >
            <PlusCircle className="h-4 w-4" />
            Add URL
          </Button>
        )}
      </div>
      
      {urls.length === 0 && !showAddForm && (
        <Alert>
          <Link className="h-4 w-4" />
          <AlertTitle>No referral URLs configured</AlertTitle>
          <AlertDescription>
            Add up to three URLs that will be used for referrals.
          </AlertDescription>
        </Alert>
      )}
      
      {urls.length > 0 && (
        <div className="space-y-4">
          {urls.map((url) => (
            <ReferralURLCard
              key={url.id}
              url={url}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onBannerUpdate={handleBannerUpdate}
            />
          ))}
        </div>
      )}
      
      {showAddForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit Referral URL' : 'Add Referral URL'}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReferralURLAddEditForm
              editingUrl={urls.find(u => u.id === editingId)}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
              maxUrlsReached={urls.length >= 3}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReferralURLForm;
